/**
 * @file ServiceDisclaimer.tsx
 * @description 시뮬레이션 결과 참고사항 컴포넌트
 *
 * 주요 기능:
 * 1. 기본 경고 형태로 시뮬레이션 참고사항 표시
 * 2. 클릭 시 Sheet로 확장하여 상세 내용 표시
 * 3. 법적 리스크 최소화를 위한 항상 표시 방식
 * 4. 모든 페이지에서 일관된 디자인/문구/위치 유지
 *
 * @dependencies
 * - @/components/shared/ui/sheet: Sheet 컴포넌트
 * - lucide-react: AlertTriangle, Info 아이콘
 */

"use client";

import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/shared/ui/sheet";

interface ServiceDisclaimerProps {
  /**
   * 컴포넌트 위치 스타일
   * - "fixed": 화면 하단 고정 (스크롤해도 항상 보임)
   * - "relative": 페이지 하단 (일반적인 위치)
   */
  position?: "fixed" | "relative";
  /**
   * 인쇄 시 표시 여부
   * - true: 인쇄 시에도 표시
   * - false: 인쇄 시 숨김 (기본값)
   */
  showOnPrint?: boolean;
}

export function ServiceDisclaimer({
  position = "fixed",
  showOnPrint = false,
}: ServiceDisclaimerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const containerClass =
    position === "fixed"
      ? "fixed bottom-0 left-0 right-0 z-40 print:hidden"
      : "relative mt-auto print:hidden";

  const printClass = showOnPrint ? "" : "print:hidden";

  return (
    <>
      {/* 기본 표시 - 경고 형태 */}
      <div
        className={`${containerClass} ${printClass} bg-amber-50/95 dark:bg-amber-950/20 backdrop-blur-sm border border-amber-200 dark:border-amber-900/50 px-4 py-3 shadow-sm mx-4 mb-4 rounded-lg`}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
              시뮬레이션 결과 참고사항
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
              본 결과는 사용자가 입력한 조건을 기준으로 계산된 시뮬레이션 정보입니다. 실제 투자 결정에 앞서 전문가와 상담하시기 바랍니다.
              <button
                onClick={() => setIsOpen(true)}
                className="ml-1 underline hover:no-underline text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                aria-label="자세한 서비스 기준 안내 보기"
              >
                자세히 보기
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Sheet 확장 */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[60vh] flex flex-col gap-4"
        >
          <SheetHeader>
            <SheetTitle className="text-base font-bold text-slate-900 dark:text-slate-50">
              시뮬레이션 결과 참고사항
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto space-y-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                    1
                  </span>
                </div>
                <div>
                  <p className="font-medium mb-1">계산 결과의 성격</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    이 서비스는 실제 권리관계, 세금, 법률 결과를 확정하지
                    않습니다. 계산 결과는 입력값과 선택한 조건에 따라 달라질 수
                    있습니다.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                    2
                  </span>
                </div>
                <div>
                  <p className="font-medium mb-1">시뮬레이션 정보</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    본 결과는 비교 및 검토 목적의 시뮬레이션 정보입니다. 실제
                    투자 결정에 앞서 전문가와 상담하시기 바랍니다.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                    3
                  </span>
                </div>
                <div>
                  <p className="font-medium mb-1">입력값의 영향</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    계산 결과는 사용자가 입력한 입찰가, 매도가, 세금 기준,
                    대출 조건 등에 따라 달라집니다. 특히 인수권리금액은 실제와
                    차이가 있을 수 있습니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                본 안내는 서비스 이용 시 참고하시기 바랍니다.
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

